/**
 * Counts page views with GoatCounter (https://www.goatcounter.com), which
 * replaced Google Analytics: no cookies, about 3 KB instead of about 197 KB.
 *
 * The script is added from the browser after the page has started, so it never
 * holds up the first paint, and only on hosts listed in src/models/analytics.ts.
 * GoatCounter's own automatic count is switched off (`no_onload`) so every
 * page view, the first one included, goes through `countPageView`.
 */

import { GOATCOUNTER_SCRIPT, goatCounterEndpoint, pageViewPath } from '../models/analytics';

interface IGoatCounter {
  endpoint?: string;
  no_onload?: boolean;
  count?: (vars: { path: string; referrer?: string }) => void;
}

declare global {
  interface Window {
    goatcounter?: IGoatCounter;
  }
}

/**
 * Sends one page view, if the script has loaded. Returns whether it was sent.
 *
 * After an in-site link, `document.referrer` still names the site the visitor
 * first came from, so the previous page is passed instead; otherwise every
 * page in a visit would be credited to Google or Instagram.
 */
export const countPageView = (location: Location, prevLocation?: Location | null): boolean => {
  const count = window.goatcounter?.count;
  if (!count) return false;
  count({
    path: pageViewPath(location),
    ...(prevLocation ? { referrer: prevLocation.href } : {}),
  });
  return true;
};

/** How long an in-site page view waits for its page's title before it is sent anyway. */
const TITLE_WAIT_MS = 1000;

/**
 * Resolves once a `<title>` other than `previous` is in the head, or after
 * TITLE_WAIT_MS. Gatsby removes the old page's title on navigation and adds the
 * new one a moment later from a separate render, so at `onRouteUpdate` the head
 * still holds the old title, and an instant later it holds none.
 */
const nextTitle = (previous: Element | null): Promise<void> =>
  new Promise(done => {
    const isNew = (): boolean => {
      const title = document.querySelector('head > title');
      return title !== null && title !== previous;
    };
    if (isNew()) return done();
    const observer = new MutationObserver(() => isNew() && finish());
    const timer = setTimeout(() => finish(), TITLE_WAIT_MS);
    const finish = (): void => {
      observer.disconnect();
      clearTimeout(timer);
      done();
    };
    observer.observe(document.head, { childList: true });
  });

/** Counts an in-site move once the new page's title is in place. */
export const countRouteChange = (location: Location, prevLocation: Location): void => {
  nextTitle(document.querySelector('head > title')).then(() => countPageView(location, prevLocation));
};

/** Loads the script for this host and counts the page the visitor landed on. */
export const startGoatCounter = (): void => {
  const endpoint = goatCounterEndpoint(window.location.hostname);
  if (!endpoint) return;

  window.goatcounter = { endpoint, no_onload: true };

  const script = document.createElement('script');
  script.src = GOATCOUNTER_SCRIPT.src;
  script.integrity = GOATCOUNTER_SCRIPT.integrity;
  script.crossOrigin = 'anonymous';
  script.async = true;
  script.onload = () => countPageView(window.location);
  document.head.appendChild(script);
};
